import { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { categoryColor, categoryLabel } from '../components/CategoryPicker'
import { Screen } from '../components/Layout'
import { Balance, Card, EmptyState, ErrorBanner, Spinner, balanceSentence } from '../components/ui'
import { ApiError } from '../lib/api'
import { currentMonth, money, monthLabel, shortDate } from '../lib/format'
import { useBootstrap, useCategories, useExpenses, useMe, useReport } from '../lib/queries'
import type { Expense, SharedWithMe } from '../lib/types'
import { useSession } from '../session'

/**
 * This month: total, the balance with each member, and the expense list
 * (requirements 2, 3 and 6).
 */
export function DashboardPage() {
  const month = currentMonth()
  const me = useMe()
  const expenses = useExpenses('month', month)
  const report = useReport('month', month, 'main')
  const taxonomy = useCategories()
  const bootstrap = useBootstrap()
  const { session } = useSession()

  /**
   * A signed-in user with no profile happens twice: the first login, if the
   * bootstrap call after the token exchange failed, and every dev impersonation,
   * which never goes through that exchange at all. Repairing it here rather than
   * only at login means neither case leaves a dead app.
   */
  const missingProfile = me.error instanceof ApiError && me.error.status === 404
  useEffect(() => {
    if (missingProfile && !bootstrap.isPending && !bootstrap.isSuccess) {
      bootstrap.mutate({ ...(session?.displayName ? { displayName: session.displayName } : {}) })
    }
  }, [missingProfile, bootstrap, session])

  const byDate = useMemo(
    () => groupByDate(expenses.data?.expenses ?? [], expenses.data?.sharedWithMe ?? []),
    [expenses.data],
  )

  if (me.isLoading || (missingProfile && bootstrap.isPending)) {
    return (
      <Screen title="VMoney">
        <Spinner label="正在準備你的帳本" />
      </Screen>
    )
  }

  return (
    <Screen
      title={monthLabel(month)}
      action={
        <Link to="/reports" className="text-sm text-accent">
          報表
        </Link>
      }
    >
      <div className="space-y-4">
        {me.error && !missingProfile ? (
          <ErrorBanner error={me.error} onRetry={() => void me.refetch()} />
        ) : null}

        <Card className="p-4">
          <p className="text-sm text-muted">本月支出</p>
          {/*
            The header counts the user's own share; each row below still shows
            what was charged, because that is the number on the statement.
          */}
          <p className="tnum mt-1 text-3xl font-semibold">
            {report.isLoading ? '—' : money(report.data?.totalMinor ?? 0)}
          </p>
          <p className="mt-1 text-xs text-muted">
            {report.data ? `${report.data.count} 筆` : ' '}
          </p>
        </Card>

        {(me.data?.members.length ?? 0) > 0 ? (
          <section>
            <h2 className="mb-2 text-sm text-muted">分帳</h2>
            <ul className="space-y-2">
              {me.data?.members.map((member) => (
                <li key={member.pairId}>
                  <Link
                    to={`/members/${member.pairId}`}
                    className="flex items-center gap-3 rounded-2xl bg-ink-soft/70 p-4 ring-1 ring-ink-line/60"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate">
                        {member.displayName}
                        {member.status === 'pending' ? (
                          <span className="ml-2 rounded bg-ink px-1.5 py-0.5 text-xs text-muted">
                            待加入
                          </span>
                        ) : null}
                      </p>
                      <p className="truncate text-xs text-muted">
                        {balanceSentence(member.netMinor, member.currency, member.displayName)}
                        {member.openSplitCount > 0 ? ` · ${member.openSplitCount} 筆未結` : ''}
                      </p>
                    </div>
                    <Balance netMinor={member.netMinor} currency={member.currency} />
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section>
          <h2 className="mb-2 text-sm text-muted">明細</h2>
          {expenses.isLoading ? <Spinner /> : null}
          {expenses.error ? (
            <ErrorBanner error={expenses.error} onRetry={() => void expenses.refetch()} />
          ) : null}
          {byDate.length === 0 && expenses.data ? (
            <EmptyState title="這個月還沒有支出" hint="按下方的 ＋ 記一筆，或從匯入頁上傳信用卡帳單。" />
          ) : null}

          {byDate.map(([date, items]) => (
            <div key={date} className="mb-4">
              <div className="mb-1 flex items-baseline justify-between text-xs text-muted">
                <span>{shortDate(date)}</span>
                {/* Own share, so the days add up to the 本月支出 figure above. */}
                <span className="tnum">
                  {money(items.reduce((sum, row) => sum + ownPart(row), 0))}
                </span>
              </div>
              <ul className="overflow-hidden rounded-2xl ring-1 ring-ink-line/60">
                {items.map((row) =>
                  row.kind === 'own' ? (
                    <li
                      key={row.expense.expenseId}
                      className="border-b border-ink-line/40 last:border-0"
                    >
                      <Link
                        to={`/expenses/${row.expense.expenseId}`}
                        className="flex items-center gap-3 bg-ink-soft/50 px-3 py-3"
                      >
                        <span
                          aria-hidden
                          className="size-2.5 shrink-0 rounded-full"
                          style={{
                            backgroundColor: categoryColor(taxonomy.data, row.expense.categoryId),
                          }}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm">
                            {row.expense.merchant ??
                              categoryLabel(
                                taxonomy.data,
                                row.expense.categoryId,
                                row.expense.subCategoryId,
                              )}
                          </p>
                          <p className="truncate text-xs text-muted">
                            {categoryLabel(
                              taxonomy.data,
                              row.expense.categoryId,
                              row.expense.subCategoryId,
                            )}
                            {row.expense.isSplit
                              ? ` · 已分帳，自付 ${money(row.expense.myShareMinor, row.expense.currency)}`
                              : ''}
                            {row.expense.source === 'import' ? ' · 匯入' : ''}
                          </p>
                        </div>
                        <span className="tnum text-sm">
                          {money(row.expense.amountMinor, row.expense.currency)}
                        </span>
                      </Link>
                    </li>
                  ) : (
                    <li
                      key={`${row.shared.pairId}#${row.shared.seq}`}
                      className="border-b border-ink-line/40 last:border-0"
                    >
                      {/*
                        The other member's expense: it opens the shared ledger,
                        not an expense page, because the expense is theirs to edit.
                        The amount is this user's share -- their spending -- and the
                        total it came out of is said underneath.
                      */}
                      <Link
                        to={`/members/${row.shared.pairId}`}
                        className="flex items-center gap-3 bg-ink-soft/50 px-3 py-3"
                      >
                        <span
                          aria-hidden
                          className="size-2.5 shrink-0 rounded-full"
                          style={{
                            backgroundColor: categoryColor(taxonomy.data, row.shared.categoryId),
                          }}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm">
                            {row.shared.note ??
                              row.shared.merchant ??
                              categoryLabel(
                                taxonomy.data,
                                row.shared.categoryId,
                                row.shared.subCategoryId,
                              )}
                          </p>
                          <p className="truncate text-xs text-muted">
                            {categoryLabel(
                              taxonomy.data,
                              row.shared.categoryId,
                              row.shared.subCategoryId,
                            )}
                            {` · ${row.shared.payerName} 付款，共 ${money(row.shared.totalMinor, row.shared.currency)}`}
                          </p>
                        </div>
                        <span className="tnum text-sm text-owing">
                          {money(row.shared.shareMinor, row.shared.currency)}
                        </span>
                      </Link>
                    </li>
                  ),
                )}
              </ul>
            </div>
          ))}
        </section>
      </div>
    </Screen>
  )
}

type Row = { kind: 'own'; expense: Expense } | { kind: 'shared'; shared: SharedWithMe }

/** What a row adds to the day total: the user's own part, either way round. */
function ownPart(row: Row): number {
  return row.kind === 'own' ? row.expense.myShareMinor : row.shared.shareMinor
}

/**
 * Newest day first, matching how a statement is read. A partner's split lands
 * on the day it was spent, among the user's own expenses, rather than in a list
 * of its own -- it is part of that day's spending.
 */
function groupByDate(expenses: Expense[], shared: SharedWithMe[]): [string, Row[]][] {
  const groups = new Map<string, Row[]>()
  const add = (date: string, row: Row) => {
    const list = groups.get(date) ?? []
    list.push(row)
    groups.set(date, list)
  }
  for (const expense of expenses) add(expense.date, { kind: 'own', expense })
  for (const item of shared) add(item.date, { kind: 'shared', shared: item })
  return [...groups.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1))
}
